import * as _ from "lodash";
import {ASync} from "../PA.Core/Basic/ASync";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {RecordUtils} from "../PA.Core/Basic/Records";
import {
    IMessageProvider,
    IProgressProvider, MessageType
} from "../PA.Core/PA.Core.IF.Basic";
import {
    IBusinessObject,
    IBusinessObjectChanges,
    IBusinessObjectChangesOptions,
    IBusinessObjectCreateLoadOptions
} from "../PA.Core/PA.Core.IF.BO";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {
    CreateOptions,
    ESRecordResolver
} from "../PA.Core/lib/ESRecordResolver"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_LE_Spend,
    esWB_LE_Spend_Partition,
    esWB_LE_Spend_Partition_Input, esWB_MasterData_Set, esWB_SalesArea, esWB_SalesOrg,
} from "../PA.Core/PAdbSchema";
import {WB_LESpendPartitionInputRow,
    WB_LESpendPartitionRow,
    WB_LESpendRow, WB_MasterDataSetRow, WB_SalesAreaRow, WB_SalesOrgRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbLESpend} from "../PA.Core/PAdbSchemaRowsIF";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {Role_User_Admin} from "../PA.Core/Security/Roles";
import {LESpendBOValidations} from "./LESpendBO.Validations";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {BehaviorSubject} from "rxjs";
import {MDUsageService} from "../PA.MD.Services/MDUsageService";

export class LESpendBO
    implements
        IBusinessObject
{

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_LESpend: WB_LESpendRow;

    _bSalesOrgEnabled: boolean
    _bSalesAreaEnabled: boolean
    _LESpendEnterpriseSelectEnabledChanged: BehaviorSubject<{ SalesOrg: boolean, SalesArea: boolean }>

    constructor(options?: IBusinessObjectCreateLoadOptions) {
        this.CreateOptions = options || {};
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New Control Point'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.drWB_LESpend = null;

        // enabled after copy, disabled after save
        this._bSalesOrgEnabled = false;
        this._bSalesAreaEnabled = false;
        this._LESpendEnterpriseSelectEnabledChanged = new BehaviorSubject<{SalesOrg: boolean; SalesArea: boolean}>(undefined);

        this.IHavePartitions_init()
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">
    get WB_LESpend(): WB_LESpendRow {
        return this.drWB_LESpend;
    }

    get bSalesOrgEnabled(): boolean {
        return this._bSalesOrgEnabled;
    }

    get bSalesAreaEnabled(): boolean {
        return this._bSalesAreaEnabled;
    }

    get LESpendEnterpriseSelectEnabledChanged(): BehaviorSubject<{ SalesOrg: boolean; SalesArea: boolean }> {
        return this._LESpendEnterpriseSelectEnabledChanged;
    }

    async LoadNew(options?: IBusinessObjectCreateLoadOptions): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Control Point");
        let olc = self._OnLoadComplete = (async () => {
            self.LoadOptions = options || {};

            try {
                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for new LE Spend Control Point.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return this;
                }

                let newLESpendRow  = <WB_LESpendRow> await this.dbContext.createRecordWithKey(esWB_LE_Spend)
                newLESpendRow.setDefaults();
                self.drWB_LESpend = newLESpendRow;
                self.dbContext.add(esWB_LE_Spend, newLESpendRow);
                self.updateIdentity();
                await self.applyCreateOptions()


                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;

                self._OnLoadComplete = undefined;
                return self;
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }

        })();
        return olc;
    }

    async Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        let olc = this._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    const sError = 'Customer Type already loaded.';
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }
                self.LoadOptions = options || {};

                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else if (_.isEmpty(self.dbContext)) {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for LE Spend.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver;
                    recordResolverLoadPromise = ASync.resolve();
                } else {
                    recordResolver = LESpendBO.createRecordResolver({dbContext: self.dbContext});
                    recordResolverLoadPromise = LESpendBO.setupRecordResolver(recordResolver, [anId]);
                }

                await recordResolverLoadPromise

                const aLESpendRow: any = recordResolver.get(esWB_LE_Spend, anId);

                if (_.isEmpty(aLESpendRow)) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception('Id not found.');
                    return self;
                }

                self.drWB_LESpend = aLESpendRow;
                self.updateIdentity();

                if (_.isNumber(self.drWB_LESpend.cif.WB_MDSET_ID) && self.drWB_LESpend.cif.WB_MDSET_ID > 0) {
                    let mruService = new MDUsageService()
                    await mruService.saveMDSetUsage(self.drWB_LESpend.cif.WB_MDSET_ID)
                }

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return self
            } catch (err: any) {
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })();

        return olc;
    }

    private updateIdentity(): void {

        const {
            WB_LE_SPEND_ID,
            CONTROL_POINT_ID,
            CONTROL_POINT_NAME
        } = this.WB_LESpend.cif;
        this.Identity.setAll(WB_LE_SPEND_ID, CONTROL_POINT_ID, CONTROL_POINT_NAME);
        this.LoadProgressProvider.setName(
            'Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
        );
        this.Identity.listenToRecordUpdates(this.WB_LESpend, 'WB_LE_SPEND_ID', 'CONTROL_POINT_ID', 'CONTROL_POINT_NAME');
        let userContext = PAEnvironment.getValue(PAEnvironment.UserContext, 'UserContext')
        let bHasRole = userContext.hasRoleId(Role_User_Admin)

        if (bHasRole)
            this.Identity.enableRecordWriteBack(this.WB_LESpend, undefined, 'CONTROL_POINT_NAME')
        else
            this.Identity.enableRecordWriteBack(this.WB_LESpend, undefined, undefined)

    }

    async applyCreateOptions() {
        const self = this
        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_LESpend.cif.CONTROL_POINT_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_LESpend.WB_MasterDataSetRow = mds
                self.WB_LESpend.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Sales Org
        let wbSalesOrg = self.CreateOptions?.wbSalesOrgId || self.LoadOptions?.wbSalesOrgId
        if (_.isNumber(wbSalesOrg)) {
            let salesOrg: WB_SalesOrgRow = <any>await self.dbContext.find(esWB_SalesOrg, wbSalesOrg)
            if (salesOrg) {
                self.WB_LESpend.cif.WB_SALESORG_ID = wbSalesOrg
            }
        }

        // Set Sales Area
        let wbSalesArea = self.CreateOptions?.wbSalesAreaId || self.LoadOptions?.wbSalesAreaId
        if (_.isNumber(wbSalesArea)) {
            let salesArea: WB_SalesAreaRow = <any>await self.dbContext.find(esWB_SalesArea, wbSalesArea)
            if (salesArea) {
                self.WB_LESpend.cif.WB_SALESAREA_ID = wbSalesArea
            }
        }
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            'Validating ' + self.WB_LESpend.cif.CUSTM_NAME + '(' + self.WB_LESpend.cif.CUSTM_ID + ')'
        )

        self.ValidateOption = options || {};

        try {
            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate Customer Type.';
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            await LESpendBOValidations.ValidateFields(self)

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();

        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);

        }
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: {bSkipValidations?: boolean}): Promise<IBusinessObject> {
        const self = this;
        const wbLESpend = <IwbLESpend>self.drWB_LESpend;
        this.SaveProgressProvider.start(
            'Saving ' + wbLESpend.CONTROL_POINT_NAME + '(' + wbLESpend.CONTROL_POINT_ID + ')'
        );

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Control Point.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            if(!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self
                }
            }

            await saveFunction(esWB_LE_Spend, self.drWB_LESpend, {})
            await saveFunction(esWB_LE_Spend_Partition, self.drWB_LESpend.WB_LESpendPartitionRows, {})
            await saveFunction(esWB_LE_Spend_Partition_Input,
                _.flatMap(self.drWB_LESpend.WB_LESpendPartitionRows,(a) => a.WB_LESpendPartitionInputRows), {})

            const updateSelectEnabled = () => {
                self._bSalesOrgEnabled = false;
                self._bSalesAreaEnabled = false;
                self.LESpendEnterpriseSelectEnabledChanged.next({
                    SalesOrg: false,
                    SalesArea: false,
                });
            };
            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                    updateSelectEnabled();
                });
            } else {
                self._bIsSaved = true;
                updateSelectEnabled();
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);

        } finally {

        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: {bSkipValidations?: boolean}): Promise<IBusinessObject> {
        const self = this;
        const wbLESpend = <IwbLESpend>this.drWB_LESpend;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            'Deleting ' + wbLESpend.CONTROL_POINT_NAME + '(' + wbLESpend.CONTROL_POINT_ID + ')'
        );

        try {
            self.drWB_LESpend.markDeleted();
            _.forEach(self.WB_LESpend.WB_LESpendPartitionRows, (a) => {
                a.markDeleted()
                _.forEach(a.WB_LESpendPartitionInputRows, (b) => {
                    b.markDeleted()
                })
            })

            if (bSaveNow)
                await self.SaveUsing(false, {bSkipValidations: true})

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            self.DeleteProgressProvider.exception(err);
        }
        return self
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this

        const src: LESpendBO = this
        const tgt = new LESpendBO({})
        await tgt.LoadNew({
            wbMasterDataSetId: src.drWB_LESpend.cif.WB_MDSET_ID,
        })

        if (!tgt.bIsLoaded) {
            throw new Error("Unable to create new LE Spend Config")
        }

        tgt.WB_LESpend.cif.CONTROL_POINT_NAME = ''
        RecordUtils.CopyProperties(tgt.dbContext, esWB_LE_Spend,
            src.drWB_LESpend, tgt.drWB_LESpend,
            [],
            ['WB_SALESORG_ID', 'WB_SALESAREA_ID', 'CONTROL_POINT_ID', 'CONTROL_POINT_NAME', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            {}
        )
        tgt.Identity.setAll(tgt.WB_LESpend.cif.WB_LE_SPEND_ID, tgt.WB_LESpend.cif.CONTROL_POINT_ID, tgt.WB_LESpend.cif.CONTROL_POINT_NAME)

        // Copy process definitions
        for (let aSrcPartitionRow of src.WB_LESpend.WB_LESpendPartitionRows) {
            if (!aSrcPartitionRow.isActive) {
                continue
            }
            const newPartitionRow: WB_LESpendPartitionRow = await tgt.addPartition(aSrcPartitionRow.cif.WB_PARTITION_ID)
            newPartitionRow.cif.ENABLED_IND = aSrcPartitionRow.cif.ENABLED_IND
            newPartitionRow.cif.MANUAL_IND = aSrcPartitionRow.cif.MANUAL_IND

            for (let aSrcPartitionInputRow of aSrcPartitionRow.WB_LESpendPartitionInputRows) {
                if (!aSrcPartitionInputRow.isActive) {
                    continue
                }
                const newPartitionInputRow: WB_LESpendPartitionInputRow = await tgt.addPartitionInput(newPartitionRow)
                RecordUtils.CopyProperties(tgt.dbContext, esWB_LE_Spend_Partition_Input,
                    aSrcPartitionInputRow, newPartitionInputRow,
                    [],
                    ['WB_LE_SPENDPR_ID', 'CREATED_DTTS','MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
                    {}
                )
            }
        }

        tgt._bSalesOrgEnabled = true
        tgt._bSalesAreaEnabled = true

        return tgt
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_LESpend, opts)
    }


    //</editor-fold>

    //<editor-fold desc = "Partition and Item Operations">

    private _PartitionsChanged: BehaviorSubject<LESpendBO>
    private _PartitionInputsChanged: BehaviorSubject<{ Record: WB_LESpendPartitionRow }>

    private IHavePartitions_init(): void {
        // Create local BehaviorSubjects
        this._PartitionsChanged = new BehaviorSubject<LESpendBO>(undefined)
        this._PartitionInputsChanged = new BehaviorSubject<{Record: WB_LESpendPartitionRow}>(undefined)
    }


    get PartitionsChanged(): BehaviorSubject<LESpendBO> {
        return this._PartitionsChanged
    }

    get PartitionInputsChanged(): BehaviorSubject<{ Record: WB_LESpendPartitionRow }> {
        return this._PartitionInputsChanged
    }


    async addPartition(wbPartitionId?: number): Promise<WB_LESpendPartitionRow> {
        const self = this

        let cpPartition
            = <WB_LESpendPartitionRow>await self.dbContext.createRecordWithKey(esWB_LE_Spend_Partition)
        cpPartition.setDefaults()

        cpPartition.cif.WB_LE_SPEND_ID = self.drWB_LESpend.cif.WB_LE_SPEND_ID
        if (_.isNumber(wbPartitionId))
            cpPartition.cif.WB_PARTITION_ID = wbPartitionId
        cpPartition.cif.SORT_ORDER = self.WB_LESpend.WB_LESpendPartitionRows.length
        // If partition is added when creating a new control point, the event bindings will happen in the next step. Skip here to avoid repetition
        self.dbContext.add(esWB_LE_Spend_Partition, cpPartition)
        let DefaultRow = _.first(_.filter(self.WB_LESpend.WB_LESpendPartitionRows, aRow => aRow.isActive && aRow.cif.WB_PARTITION_ID == -1))
        if (!_.isEmpty(DefaultRow))
            DefaultRow.cif.SORT_ORDER = self.WB_LESpend.WB_LESpendPartitionRows.length
        self.PartitionsChanged.next(this)
        return cpPartition
    }

    async removePartition(wbLESpendPRId: number): Promise<void> {
        const self = this

        _.forEach(self.WB_LESpend.WB_LESpendPartitionRows, (aRow) => {
            if (!aRow.isActive)
                return

            if (aRow.cif.WB_LE_SPENDPR_ID == wbLESpendPRId) {
                aRow.markDeleted()
                _.forEach(aRow.WB_LESpendPartitionInputRows, (aInputRow) => {
                    if (!aInputRow.isActive)
                        return

                    aInputRow.markDeleted()
                })
            }
        })

        self.PartitionsChanged.next(this)
    }

    async addPartitionInput(leSPRow: WB_LESpendPartitionRow, wbEvtSpendTypeId?: number): Promise<WB_LESpendPartitionInputRow> {
        const self = this

        let cpPartitionInput
            = <WB_LESpendPartitionInputRow>await self.dbContext.createRecordWithKey(esWB_LE_Spend_Partition_Input)
        cpPartitionInput.setDefaults()

        cpPartitionInput.cif.WB_LE_SPENDPR_ID = leSPRow.cif.WB_LE_SPENDPR_ID
        cpPartitionInput.WB_LESpendPartitionRow = leSPRow

        if (_.isNumber(wbEvtSpendTypeId))
            cpPartitionInput.cif.WB_EVTSTYPE_ID = wbEvtSpendTypeId


        self.dbContext.add(esWB_LE_Spend_Partition_Input, cpPartitionInput)

        self.PartitionInputsChanged.next({Record: leSPRow})
        return cpPartitionInput
    }

    async removePartitionInput(wbLESpendPSRId: number): Promise<void> {
        const self = this

        _.forEach(self.WB_LESpend.WB_LESpendPartitionRows, (aRow) => {
            if (!aRow.isActive)
                return

            _.forEach(aRow.WB_LESpendPartitionInputRows, (aRow) => {
                if (aRow.cif.WB_LE_SPENDPSR_ID == wbLESpendPSRId) {
                    aRow.markDeleted()
                }
            })
        })

        self.PartitionInputsChanged.next({Record: undefined})

    }


    //</editor-fold>


    //<editor-fold desc = "staticIRecordResolver">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const createOptions: CreateOptions = {
            standardConditions: {
                [esWB_LE_Spend]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_LE_Spend_Partition]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_LE_Spend_Partition_Input]: {"DELETED_IND": {"notIn": ['1']}},
            },
            dctIncludeBelongsToNamedRelations: { }
        };

        return new ESRecordResolver(dbContext, createOptions);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_LE_Spend, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(LESpendBO, "LESpendBO", "LE Spend")
